import React from 'react';
import ReactDOM from 'react-dom';
import { AppContainer } from 'react-hot-loader';
import { createStore, compose, applyMiddleware } from 'redux';
import thunk from 'redux-thunk';
import { routerMiddleware } from 'connected-react-router';
import { persistStore, persistReducer, createMigrate } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { composeWithDevTools } from 'redux-devtools-extension';
import promise from 'redux-promise';
import * as Sentry from '@sentry/react';
import mixpanel from 'mixpanel-browser';
import dotenv from 'dotenv';
import createRootReducer from '../reducers';
import Root from '../containers/Root';
import postConfigRoutingMiddleware from '../middlewares/postConfigRoutingMiddleware';
import history from '../utils/history';

require('react-hot-loader/patch');

dotenv.config();

if (
  process.env.REACT_SENTRY_DSN &&
  process.env.REACT_SENTRY_TRACE_SAMPLE_RATE
) {
  Sentry.init({
    dsn: process.env.REACT_SENTRY_DSN,
    replaysSessionSampleRate: parseFloat(
      process.env.REACT_SENTRY_TRACE_SAMPLE_RATE,
    ),
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      new Sentry.Replay({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: parseFloat(process.env.REACT_SENTRY_TRACE_SAMPLE_RATE),
    normalizeDepth: 10,
  });
}

const MIXPANEL_TOKENS = {
  prod: 'cfe83be0101fed901abe64885635b1d9',
  stage: '74a7b3d61e4a319eb60eaa307d4e7628',
  dev: '28dc4f492427ee4a9384346c8d53fc2d',
};

// Default to dev if REACT_MIXPANEL_ENV is not set or doesn't match any key in MIXPANEL_TOKENS
const mixpanelToken =
  MIXPANEL_TOKENS[process.env.REACT_MIXPANEL_ENV] || MIXPANEL_TOKENS.dev;

console.log('Mixpanel token:', mixpanelToken);

mixpanel.init(mixpanelToken, {
  debug: true,
  track_pageview: true,
  persistence: 'localStorage',
  ignore_dnt: true,
});

// Redux Persist Migrations
// Migration from version 1 to 2: Ensures company.token is set
// Migration from version 2 to 3: Drop persisted company slice — bootstrap re-injects it every load
// Migration from version 3 to 4: Drop the persisted `shoot` slice. It caches
//   the product catalog (shootProducts -> product_option_sets, including the
//   `selection_required` flag). When an admin edited a product option set, a
//   soft refresh rehydrated the stale catalog from localStorage and the
//   customer kept seeing the old "None" option / dropdown values. Dropping the
//   slice forces a fresh fetch via getShootData on the next dashboard / key
//   entry. See ENG-2067 QA.
const migrations = {
  2: (state) => {
    // If company.token doesn't exist but company.name does, set token to match name
    if (state.company && state.company.name && !state.company.token) {
      return {
        ...state,
        company: {
          ...state.company,
          token: state.company.name, // Set token to match the existing name value
        },
      };
    }
    return state;
  },
  3: (state) => {
    if (!state) return state;
    const { company, ...rest } = state;
    return rest;
  },
  4: (state) => {
    if (!state) return state;
    const { shoot, ...rest } = state;
    return rest;
  },
};

const persistConfig = {
  key: 'root',
  storage,
  version: 4,
  migrate: createMigrate(migrations, { debug: false }),
  blacklist: ['company'],
};

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

const middleware = [
  promise,
  routerMiddleware(history),
  thunk,
  postConfigRoutingMiddleware({ history }),
];

// const middleware = [promise];
const persistedReducer = persistReducer(
  persistConfig,
  createRootReducer(history),
);

const store = createStore(
  persistedReducer,
  composeEnhancers(applyMiddleware(...middleware)),
);

document.addEventListener('DOMContentLoaded', () => {
  const rootDiv = document.querySelector('#root');
  const containerDiv = rootDiv.appendChild(document.createElement('div'));

  const configData = document.querySelector('#config-data');
  const data = JSON.parse(configData.getAttribute('data-config'));

  // Set company token in Redux - this is now used for both CSS and business logic
  // Since we've standardized css field to match token in site_settings_by_domain.yml,
  // we can now use a single identifier for everything
  const companyIdentifier = data.token || data.css || data.auth_token;

  if (companyIdentifier) {
    // Set token (preferred identifier)
    store.dispatch({
      type: 'SET_COMPANY_TOKEN',
      payload: companyIdentifier,
    });

    // Also set company.name for backward compatibility during migration
    // This can be removed once all components are updated to use company.token
    store.dispatch({
      type: 'SET_COMPANY_NAME',
      payload: companyIdentifier,
    });
  }

  if (data.non_us_internationalization_enabled !== undefined) {
    store.dispatch({
      type: 'SET_COMPANY_NON_US_INTERNATIONALIZATION_ENABLED',
      payload: !!data.non_us_internationalization_enabled,
    });
  }

  ReactDOM.render(
    <AppContainer>
      <Root
        {...data}
        history={history}
        store={store}
        persistor={persistStore(store)}
      />
    </AppContainer>,
    containerDiv,
  );

  if (module.hot) {
    module.hot.accept('../containers/Root', () => {
      const newCreateStoreWithMiddleware = composeWithDevTools(
        applyMiddleware(...middleware),
      )(createStore);
      const newStore = newCreateStoreWithMiddleware(persistedReducer);
      const NewRoot = require('../containers/Root').default;

      ReactDOM.render(
        <AppContainer>
          <NewRoot store={newStore} persistor={persistStore(newStore)} />
        </AppContainer>,
        containerDiv,
      );
    });
  }
});
